import { c as _c } from "react/compiler-runtime";
import React, { memo, useMemo } from 'react';
import { FormInputNumber, InputWithSelects, InputWithSelectsTrigger, Select } from 'shared/components';
import { FieldErrorAndDescription, FieldErrorAndDescriptionProps } from 'shared/components/form/field-error-and-description';
import { FieldLabel, FieldLabelProps } from 'shared/components/form/field-label';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twMerge } from 'tailwind-merge';
import { TimeType, TimeTypeState } from './constants';
import { createTimeTypeOptionsForSelect } from './utils';
export type TimeDelayPickerProps = Omit<React.HTMLProps<HTMLLabelElement>, 'onChange' | 'label' | 'max'> & FieldLabelProps & FieldErrorAndDescriptionProps & {
  selectedTimeValue: number;
  selectedTimeType: TimeType;
  selectedTimeBeforeAfter?: TimeTypeState;
  onChange: (selectedTimeValue: TimeDelayPickerProps['selectedTimeValue'], selectedTimeType: TimeDelayPickerProps['selectedTimeType'], selectedTimeBeforeAfter?: TimeDelayPickerProps['selectedTimeBeforeAfter']) => void;
  classNameInputInteger?: string;
  withTimeAfterBeforeSelect?: boolean;
  isPreFetching?: boolean;
  allowedOptions?: TimeType[];
  max?: number;
};
const TimeDelayPicker = memo(function TimeDelayPicker(t0) {
  const $ = _c(98);
  let allowedOptions;
  let className;
  let classNameInputInteger;
  let description;
  let disabled;
  let error;
  let isPreFetching;
  let label;
  let labelClassName;
  let max;
  let onChange;
  let props;
  let required;
  let selectedTimeBeforeAfter;
  let selectedTimeType;
  let selectedTimeValue;
  let withTimeAfterBeforeSelect;
  if ($[0] !== t0) {
    ({
      description,
      error,
      selectedTimeValue,
      selectedTimeType,
      onChange,
      labelClassName,
      className,
      label,
      required,
      selectedTimeBeforeAfter,
      withTimeAfterBeforeSelect,
      classNameInputInteger,
      isPreFetching,
      allowedOptions,
      disabled,
      max,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = allowedOptions;
    $[2] = className;
    $[3] = classNameInputInteger;
    $[4] = description;
    $[5] = disabled;
    $[6] = error;
    $[7] = isPreFetching;
    $[8] = label;
    $[9] = labelClassName;
    $[10] = max;
    $[11] = onChange;
    $[12] = props;
    $[13] = required;
    $[14] = selectedTimeBeforeAfter;
    $[15] = selectedTimeType;
    $[16] = selectedTimeValue;
    $[17] = withTimeAfterBeforeSelect;
  } else {
    allowedOptions = $[1];
    className = $[2];
    classNameInputInteger = $[3];
    description = $[4];
    disabled = $[5];
    error = $[6];
    isPreFetching = $[7];
    label = $[8];
    labelClassName = $[9];
    max = $[10];
    onChange = $[11];
    props = $[12];
    required = $[13];
    selectedTimeBeforeAfter = $[14];
    selectedTimeType = $[15];
    selectedTimeValue = $[16];
    withTimeAfterBeforeSelect = $[17];
  }
  const {
    t,
    tICU
  } = useLocoTranslation();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[18] !== allowedOptions || $[19] !== className || $[20] !== classNameInputInteger || $[21] !== disabled || $[22] !== isPreFetching || $[23] !== label || $[24] !== labelClassName || $[25] !== max || $[26] !== onChange || $[27] !== props || $[28] !== required || $[29] !== selectedTimeBeforeAfter || $[30] !== selectedTimeType || $[31] !== selectedTimeValue || $[32] !== t || $[33] !== tICU || $[34] !== withTimeAfterBeforeSelect) {
    const timeStates = [{
      id: TimeTypeState.before,
      caption: t("dashboard.funnel.emails_webinar_start_before")
    }, {
      id: TimeTypeState.after,
      caption: t("dashboard.funnel.emails_webinar_start_after")
    }];
    let t8;
    if ($[43] !== allowedOptions || $[44] !== selectedTimeValue || $[45] !== tICU) {
      t8 = createTimeTypeOptionsForSelect(tICU, selectedTimeValue, allowedOptions);
      $[43] = allowedOptions;
      $[44] = selectedTimeValue;
      $[45] = tICU;
      $[46] = t8;
    } else {
      t8 = $[46];
    }
    const typeTypeOptions = t8;
    t5 = props;
    if ($[47] !== className) {
      t6 = twMerge("flex flex-col gap-2 text-sm font-medium", className);
      $[47] = className;
      $[48] = t6;
    } else {
      t6 = $[48];
    }
    if ($[49] !== label || $[50] !== labelClassName || $[51] !== required) {
      t7 = <FieldLabel label={label} labelClassName={labelClassName} required={required} />;
      $[49] = label;
      $[50] = labelClassName;
      $[51] = required;
      $[52] = t7;
    } else {
      t7 = $[52];
    }
    T0 = InputWithSelects;
    t1 = "items-center";
    let t9;
    if ($[53] !== onChange || $[54] !== selectedTimeBeforeAfter || $[55] !== selectedTimeValue) {
      t9 = value => onChange(selectedTimeValue, value as TimeType, selectedTimeBeforeAfter);
      $[53] = onChange;
      $[54] = selectedTimeBeforeAfter;
      $[55] = selectedTimeValue;
      $[56] = t9;
    } else {
      t9 = $[56];
    }
    let t10;
    if ($[57] !== selectedTimeType || $[58] !== t || $[59] !== typeTypeOptions) {
      t10 = typeTypeOptions.find(option => Object.is(option.id, selectedTimeType))?.caption ?? t("global.select");
      $[57] = selectedTimeType;
      $[58] = t;
      $[59] = typeTypeOptions;
      $[60] = t10;
    } else {
      t10 = $[60];
    }
    let t11;
    if ($[61] !== t10) {
      t11 = <InputWithSelectsTrigger side="end" size="sm">{t10}</InputWithSelectsTrigger>;
      $[61] = t10;
      $[62] = t11;
    } else {
      t11 = $[62];
    }
    if ($[63] !== disabled || $[64] !== isPreFetching || $[65] !== selectedTimeType || $[66] !== t11 || $[67] !== t9 || $[68] !== typeTypeOptions) {
      t2 = <Select disabled={disabled} data={typeTypeOptions} value={selectedTimeType} isPreFetching={isPreFetching} onChange={t9} trigger={t11} />;
      $[63] = disabled;
      $[64] = isPreFetching;
      $[65] = selectedTimeType;
      $[66] = t11;
      $[67] = t9;
      $[68] = typeTypeOptions;
      $[69] = t2;
    } else {
      t2 = $[69];
    }
    let t12;
    if ($[70] !== classNameInputInteger) {
      t12 = twMerge("w-3/12 [&_input]:field-sizing-content [&_input]:min-w-[3ch] [&_input]:basis-auto", classNameInputInteger);
      $[70] = classNameInputInteger;
      $[71] = t12;
    } else {
      t12 = $[71];
    }
    let t13;
    if ($[72] !== onChange || $[73] !== selectedTimeBeforeAfter || $[74] !== selectedTimeType) {
      t13 = value_0 => onChange(value_0, selectedTimeType, selectedTimeBeforeAfter);
      $[72] = onChange;
      $[73] = selectedTimeBeforeAfter;
      $[74] = selectedTimeType;
      $[75] = t13;
    } else {
      t13 = $[75];
    }
    if ($[76] !== disabled || $[77] !== isPreFetching || $[78] !== max || $[79] !== selectedTimeValue || $[80] !== t12 || $[81] !== t13) {
      t3 = <FormInputNumber size="sm" disabled={disabled} className={t12} value={selectedTimeValue} onChange={t13} isPreFetching={isPreFetching} type="integer" max={max} />;
      $[76] = disabled;
      $[77] = isPreFetching;
      $[78] = max;
      $[79] = selectedTimeValue;
      $[80] = t12;
      $[81] = t13;
      $[82] = t3;
    } else {
      t3 = $[82];
    }
    t4 = withTimeAfterBeforeSelect ? <Select disabled={disabled} data={timeStates} isPreFetching={isPreFetching} value={selectedTimeBeforeAfter} onChange={value_1 => onChange(selectedTimeValue, selectedTimeType, value_1 as TimeTypeState)} trigger={<InputWithSelectsTrigger side="start" size="sm">{timeStates.find(option_0 => Object.is(option_0.id, selectedTimeBeforeAfter))?.caption ?? t("global.select")}</InputWithSelectsTrigger>} /> : undefined;
    $[18] = allowedOptions;
    $[19] = className;
    $[20] = classNameInputInteger;
    $[21] = disabled;
    $[22] = isPreFetching;
    $[23] = label;
    $[24] = labelClassName;
    $[25] = max;
    $[26] = onChange;
    $[27] = props;
    $[28] = required;
    $[29] = selectedTimeBeforeAfter;
    $[30] = selectedTimeType;
    $[31] = selectedTimeValue;
    $[32] = t;
    $[33] = tICU;
    $[34] = withTimeAfterBeforeSelect;
    $[35] = T0;
    $[36] = t1;
    $[37] = t2;
    $[38] = t3;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
  } else {
    T0 = $[35];
    t1 = $[36];
    t2 = $[37];
    t3 = $[38];
    t4 = $[39];
    t5 = $[40];
    t6 = $[41];
    t7 = $[42];
  }
  let t8;
  if ($[83] !== T0 || $[84] !== t1 || $[85] !== t2 || $[86] !== t3 || $[87] !== t4) {
    t8 = <T0 className={t1} endSelect={t2} input={t3} startSelect={t4} />;
    $[83] = T0;
    $[84] = t1;
    $[85] = t2;
    $[86] = t3;
    $[87] = t4;
    $[88] = t8;
  } else {
    t8 = $[88];
  }
  let t9;
  if ($[89] !== description || $[90] !== error) {
    t9 = <FieldErrorAndDescription description={description} error={error} />;
    $[89] = description;
    $[90] = error;
    $[91] = t9;
  } else {
    t9 = $[91];
  }
  let t10;
  if ($[92] !== t5 || $[93] !== t6 || $[94] !== t7 || $[95] !== t8 || $[96] !== t9) {
    t10 = <label {...t5} className={t6}>{t7}{t8}{t9}</label>;
    $[92] = t5;
    $[93] = t6;
    $[94] = t7;
    $[95] = t8;
    $[96] = t9;
    $[97] = t10;
  } else {
    t10 = $[97];
  }
  return t10;
});
export default TimeDelayPicker;